import { useCallback, useMemo, useState } from 'react';
import {
  Button,
  ClickAwayListener,
  ControllerAlert,
  ControllerModal,
  DialogActions,
  DialogContent,
  IconButton,
  MdCopyAll,
  OutlinedInput,
  Stack,
  Typography,
  Tooltip,
} from '@bymason-private/legacy-pattern-components';
import { CreateApiKeyResponse } from '@bymason/controller-api-hooks';

interface ApiKeyCreationSuccessModalProps {
  open: boolean;
  apiKey: CreateApiKeyResponse;
  onClose: () => void;
}

export function ApiKeyCreationSuccessModal({ apiKey, onClose, open }: ApiKeyCreationSuccessModalProps) {
  const [showCopied, setShowCopied] = useState(false);

  const handleTooltipClose = () => {
    setShowCopied(false);
  };

  const additionalTooltipProps = useMemo(
    () =>
      showCopied
        ? {
            title: 'Copied!',
            onClose: handleTooltipClose,
            open: showCopied,
          }
        : {},
    [showCopied]
  );

  const handleCopyToClipboard = useCallback(() => {
    apiKey && navigator.clipboard.writeText(apiKey.secret);
    setShowCopied(true);
  }, [apiKey]);

  return (
    <ControllerModal onClose={onClose} open={open} title='API key creation successful'>
      <DialogContent>
        <Stack py={2} direction='column' spacing={3}>
          <Typography>Please save and/or store this key somewhere safe</Typography>
          <OutlinedInput
            className='Mui-disabled'
            readOnly
            defaultValue={apiKey.secret}
            fullWidth
            endAdornment={
              <ClickAwayListener onClickAway={handleTooltipClose}>
                <Tooltip title='Click to Copy' placement='top' {...additionalTooltipProps}>
                  <IconButton onClick={handleCopyToClipboard}>
                    <MdCopyAll />
                  </IconButton>
                </Tooltip>
              </ClickAwayListener>
            }
          />
          <ControllerAlert
            sx={{
              width: '100%',
              mt: 2,
            }}
            variant='outlined'
            severity='error'
          >
            This is the only time we will be showing you this token
          </ControllerAlert>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button color='primary' size='medium' variant='contained' onClick={onClose}>
          Close
        </Button>
      </DialogActions>
    </ControllerModal>
  );
}
